import React, { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { Bell, CheckCircle2, AlertTriangle, AlertCircle, Inbox, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

const kindIcon = {
  low_stock: { Icon: AlertTriangle, color: "#C45B12", bg: "#F9ECE3" },
  price_hike: { Icon: TrendingUp, color: "#A12B2B", bg: "#F5E4E4" },
  request: { Icon: Inbox, color: "#7A2E2E", bg: "#F4E5E5" },
  success: { Icon: CheckCircle2, color: "#4A6E46", bg: "#EAF0E9" },
  error: { Icon: AlertCircle, color: "#A12B2B", bg: "#F5E4E4" },
  info: { Icon: Bell, color: "#5C5A58", bg: "#F4F3EF" },
};

export default function NotificationBell({ testIdPrefix = "notif" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {/* ignore */}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const unreadCount = items.filter((n) => !n.read).length;

  const open_ = (n) => {
    api.post(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    if (n.link) navigate(n.link);
    load();
  };

  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    load();
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-lg border border-[#E8E6E1] flex items-center justify-center hover:bg-[#F4F3EF]"
        data-testid={`${testIdPrefix}-bell`}
      >
        <Bell size={16} className="text-[#5C5A58]" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#A12B2B] text-white text-[10px] font-bold flex items-center justify-center"
            data-testid={`${testIdPrefix}-count`}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid={`${testIdPrefix}-dismiss`} />
          <div
            className="absolute right-0 mt-2 w-80 ck-card overflow-hidden z-50"
            data-testid={`${testIdPrefix}-panel`}
          >
            <div className="px-4 py-3 border-b border-[#E8E6E1] flex items-center justify-between">
              <div className="font-display font-bold">Notifications</div>
              {unreadCount > 0 && (
                <button onClick={markAll} className="text-xs text-[#7A2E2E] hover:underline" data-testid={`${testIdPrefix}-mark-all-read`}>
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-96 overflow-auto">
              {items.length === 0 && (
                <div className="text-sm text-[#8A8782] p-6 text-center">No notifications yet.</div>
              )}
              {items.map((n) => {
                const k = kindIcon[n.kind] || kindIcon.info;
                const { Icon } = k;
                return (
                  <button
                    key={n.id}
                    data-testid={`${testIdPrefix}-item-${n.id}`}
                    onClick={() => open_(n)}
                    className={`w-full text-left px-4 py-3 border-b border-[#E8E6E1] hover:bg-[#F9F8F6] flex gap-3 ${n.read ? "opacity-60" : ""}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: k.bg, color: k.color }}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm leading-snug">{n.message}</div>
                      <div className="text-xs text-[#8A8782] mt-1">{fmtDateTime(n.created_at)}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#7A2E2E] mt-1.5 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
